import type { EquipmentRecommendation } from '@freedom/rf-engine';
import type { AntennaModelRecord, DesignSite, RadioDeviceRecord } from '@freedom/shared';
import type { PtMpGroupModel, RadioLinkModel } from './radio-workspace';

export const STANDARD_CHANNEL_WIDTHS_MHZ = [10, 20, 30, 40, 50, 60, 80, 160, 1_080, 2_160] as const;
export const UNVERIFIED_TX_SAFETY_CEILING_DBM = 60;

export function txPowerOptions(currentPowerDbm: number, maximumPowerDbm: number | null, unverifiedControlCeilingDbm = currentPowerDbm): number[] {
  const upper = Math.floor(maximumPowerDbm ?? Math.max(currentPowerDbm, unverifiedControlCeilingDbm));
  const values = Array.from({ length: Math.max(0, upper + 11) }, (_, index) => upper - index);
  if (!values.includes(currentPowerDbm)) values.push(currentPowerDbm);
  return values.sort((left, right) => right - left);
}

export function displayEquipmentName(value: string): string {
  return value.trim().replace(/^Ubiquiti(?:\s+|$)/iu, '').trim();
}

export interface PublishedLinkRangeLimit {
  maximumKm: number;
  equipmentName: string;
}

export function publishedLinkRangeLimit(endpoints: readonly DesignSite[], radios: readonly RadioDeviceRecord[]): PublishedLinkRangeLimit | null {
  const limits = endpoints.flatMap((site): PublishedLinkRangeLimit[] => {
    const catalogRadio = site.radioDeviceId ? radios.find((radio) => radio.id === site.radioDeviceId) : undefined;
    const maximumKm = site.radioMaxRangeKm === undefined ? catalogRadio?.maxRangeKm ?? null : site.radioMaxRangeKm;
    if (maximumKm === null || !Number.isFinite(maximumKm) || maximumKm <= 0) return [];
    return [{ maximumKm, equipmentName: displayEquipmentName(catalogRadio?.model ?? site.equipmentName) }];
  });
  return limits.sort((left, right) => left.maximumKm - right.maximumKm)[0] ?? null;
}

export function channelWidthOptions(currentWidthMHz: number, supportedWidthsMHz?: readonly number[]): number[] {
  const candidates = supportedWidthsMHz === undefined ? STANDARD_CHANNEL_WIDTHS_MHZ : supportedWidthsMHz;
  return [...new Set([...candidates, currentWidthMHz])]
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right);
}

export function mutuallySupportedChannelWidths(sites: DesignSite[], targetSiteIds: readonly string[]): number[] {
  if (targetSiteIds.length === 0) return [];
  let shared: Set<number> | undefined;
  for (const siteId of targetSiteIds) {
    const site = sites.find((candidate) => candidate.id === siteId);
    const widths = site?.radioChannelWidthsMHz?.filter((value) => Number.isFinite(value) && value > 0);
    if (!widths || widths.length === 0) return [];
    const supported = new Set(widths);
    if (shared === undefined) shared = supported;
    else {
      const previous = shared;
      shared = new Set([...previous].filter((value) => supported.has(value)));
    }
  }
  return [...(shared ?? [])].sort((left, right) => left - right);
}

export function patchLinkChannelWidth(
  sites: DesignSite[],
  siteId: string,
  channelWidthMHz: number,
  selectedLink: RadioLinkModel | null,
  ptmpGroups: PtMpGroupModel[],
): DesignSite[] {
  if (!Number.isFinite(channelWidthMHz) || channelWidthMHz <= 0) return sites;
  const targetSiteIds = new Set([siteId]);
  if (selectedLink && (selectedLink.startSiteId === siteId || selectedLink.endSiteId === siteId)) {
    targetSiteIds.add(selectedLink.startSiteId);
    targetSiteIds.add(selectedLink.endSiteId);
    if (selectedLink.groupId) {
      const group = ptmpGroups.find((candidate) => candidate.id === selectedLink.groupId);
      if (group) {
        targetSiteIds.add(group.apSiteId);
        group.stationSiteIds.forEach((stationId) => targetSiteIds.add(stationId));
      }
    }
  }
  if (selectedLink?.productSelectionMode === 'manual'
    && !mutuallySupportedChannelWidths(sites, [...targetSiteIds]).includes(channelWidthMHz)) return sites;
  let changed = false;
  const updated = sites.map((site) => {
    if (!targetSiteIds.has(site.id) || site.channelWidthMHz === channelWidthMHz) return site;
    changed = true;
    return { ...site, channelWidthMHz };
  });
  return changed ? updated : sites;
}

export function automaticEquipmentTargetSiteIds(selectedLink: RadioLinkModel | null, ptmpGroups: PtMpGroupModel[]): string[] {
  if (!selectedLink) return [];
  if (!selectedLink.groupId) return [...new Set([selectedLink.startSiteId, selectedLink.endSiteId])];
  const group = ptmpGroups.find((candidate) => candidate.id === selectedLink.groupId);
  return group ? [...new Set([group.apSiteId, ...group.stationSiteIds])] : [...new Set([selectedLink.startSiteId, selectedLink.endSiteId])];
}

export interface RadioAntennaCompatibility {
  status: 'compatible' | 'unknown' | 'incompatible';
  reason: string | null;
}

export function assessRadioAntennaCompatibility(radio: RadioDeviceRecord, antenna: AntennaModelRecord, frequencyMHz: number): RadioAntennaCompatibility {
  if (!radio.isActive) return { status: 'incompatible', reason: 'радио неактивно в каталоге' };
  if (!antenna.isActive) return { status: 'incompatible', reason: 'антенна неактивна в каталоге' };
  if (radio.antennaIntegrated) return { status: 'incompatible', reason: 'у радио встроенная антенна' };
  if (antenna.peakGainDbi === null || !Number.isFinite(antenna.peakGainDbi)) return { status: 'incompatible', reason: 'усиление антенны не подтверждено' };
  if (!Number.isFinite(frequencyMHz)) return { status: 'incompatible', reason: 'рабочая частота не задана' };
  if (radio.minFrequencyMHz !== null && radio.maxFrequencyMHz !== null
    && (frequencyMHz < radio.minFrequencyMHz || frequencyMHz > radio.maxFrequencyMHz)) {
    return { status: 'incompatible', reason: 'рабочая частота вне диапазона радио' };
  }
  if (antenna.minFrequencyMHz !== null && antenna.maxFrequencyMHz !== null
    && (frequencyMHz < antenna.minFrequencyMHz || frequencyMHz > antenna.maxFrequencyMHz)) {
    return { status: 'incompatible', reason: 'антенна не поддерживает рабочую частоту' };
  }
  const directFamilyMatch = radio.compatibilityTags.some((tag) => antenna.compatibilityTags.includes(tag));
  const airFiberAdapterMatch = radio.compatibilityTags.includes('airfiber-x') && antenna.compatibilityTags.includes('airfiber-x-adapter');
  const waveMloAdapterMatch = radio.compatibilityTags.includes('wave-mlo') && antenna.compatibilityTags.includes('wave-mlo-adapter');
  if (radio.compatibilityTags.length > 0 && antenna.compatibilityTags.length > 0
    && !directFamilyMatch && !airFiberAdapterMatch && !waveMloAdapterMatch) {
    return { status: 'incompatible', reason: 'несовместимые семейства крепления/радио' };
  }

  const unknownReasons: string[] = [];
  if (radio.minFrequencyMHz === null || radio.maxFrequencyMHz === null) unknownReasons.push('диапазон радио не подтверждён');
  if (antenna.minFrequencyMHz === null || antenna.maxFrequencyMHz === null) unknownReasons.push('диапазон антенны не подтверждён');
  if (radio.compatibilityTags.length === 0 || antenna.compatibilityTags.length === 0) unknownReasons.push('каталожная совместимость крепления не подтверждена');
  if (airFiberAdapterMatch) unknownReasons.push('требуется универсальный кронштейн или комплект AF-5G-OMT-S45');
  if (waveMloAdapterMatch) unknownReasons.push('для подключения к Wave MLO требуется совместимый RF-переходник или кабель');
  return unknownReasons.length > 0
    ? { status: 'unknown', reason: unknownReasons.join('; ') }
    : { status: 'compatible', reason: null };
}

export function radioAntennaCompatibilityReason(radio: RadioDeviceRecord, antenna: AntennaModelRecord, frequencyMHz: number): string | null {
  const compatibility = assessRadioAntennaCompatibility(radio, antenna, frequencyMHz);
  return compatibility.status === 'incompatible' ? compatibility.reason : null;
}

export function radioAntennaCompatibilityWarning(radio: RadioDeviceRecord, antenna: AntennaModelRecord, frequencyMHz: number): string | null {
  const compatibility = assessRadioAntennaCompatibility(radio, antenna, frequencyMHz);
  return compatibility.status === 'unknown' ? compatibility.reason : null;
}

export function siteRadioDevice(site: DesignSite, radios: RadioDeviceRecord[]): RadioDeviceRecord | null {
  if (site.radioDeviceId) return radios.find((radio) => radio.id === site.radioDeviceId) ?? null;
  const equipmentName = normalizedEquipmentName(site.equipmentName);
  return radios.find((radio) => normalizedEquipmentName(`${radio.manufacturerName} ${radio.model}`) === equipmentName
    || normalizedEquipmentName(radio.model) === equipmentName) ?? null;
}

export function siteAntennaCompatibilityReason(site: DesignSite, antenna: AntennaModelRecord, radios: RadioDeviceRecord[]): string | null {
  const radio = siteRadioDevice(site, radios);
  return radio ? radioAntennaCompatibilityReason(radio, antenna, site.frequencyGHz * 1_000) : 'сначала выберите радио с подтверждёнными RF-данными';
}

export function siteAntennaCompatibilityWarning(site: DesignSite, antenna: AntennaModelRecord, radios: RadioDeviceRecord[]): string | null {
  const radio = siteRadioDevice(site, radios);
  return radio ? radioAntennaCompatibilityWarning(radio, antenna, site.frequencyGHz * 1_000) : null;
}

export function antennaPatchForRadioChange(
  site: DesignSite,
  radio: RadioDeviceRecord,
  antennas: AntennaModelRecord[],
  frequencyMHz: number,
): Pick<DesignSite, 'antennaModelId' | 'antennaName' | 'antennaGainDbi'> {
  if (radio.antennaIntegrated) return { antennaModelId: null, antennaName: null, antennaGainDbi: radio.antennaGainDbi ?? 0 };
  const existing = site.antennaModelId ? antennas.find((antenna) => antenna.id === site.antennaModelId) : null;
  if (existing && assessRadioAntennaCompatibility(radio, existing, frequencyMHz).status === 'compatible') {
    return { antennaModelId: existing.id, antennaName: existing.model, antennaGainDbi: existing.peakGainDbi ?? site.antennaGainDbi };
  }
  return { antennaModelId: null, antennaName: null, antennaGainDbi: 0 };
}

function recommendationForSite(
  siteId: string,
  startSiteId: string,
  recommendation: EquipmentRecommendation,
  peerRecommendation: EquipmentRecommendation | null,
): EquipmentRecommendation | null {
  return siteId === startSiteId ? recommendation : peerRecommendation;
}

function numberArraysEqual(left: number[] | undefined, right: number[]): boolean {
  return left !== undefined && left.length === right.length && left.every((value, index) => value === right[index]);
}

function performanceProfilesEqual(left: DesignSite['radioPerformanceProfiles'], right: EquipmentRecommendation['performanceProfiles']): boolean {
  return left !== undefined && left.length === right.length && left.every((profile, profileIndex) => {
    const other = right[profileIndex];
    return other !== undefined
      && profile.source === other.source
      && profile.channelWidthMHz === other.channelWidthMHz
      && profile.entries.length === other.entries.length
      && profile.entries.every((entry, entryIndex) => {
        const otherEntry = other.entries[entryIndex];
        return otherEntry !== undefined
          && entry.name === otherEntry.name
          && entry.modulation === otherEntry.modulation
          && entry.sensitivityDbm === otherEntry.sensitivityDbm
          && entry.minimumSnrDb === otherEntry.minimumSnrDb
          && entry.phyRateMbps === otherEntry.phyRateMbps
          && entry.usableThroughputMbps === otherEntry.usableThroughputMbps;
      });
  });
}

function siteMatchesRecommendationSnapshot(site: DesignSite, next: DesignSite, selected: EquipmentRecommendation): boolean {
  return site.equipmentName === next.equipmentName
    && site.antennaGainDbi === next.antennaGainDbi
    && site.txPowerDbm === next.txPowerDbm
    && site.frequencyGHz === next.frequencyGHz
    && site.channelWidthMHz === next.channelWidthMHz
    && site.radioDeviceId === selected.deviceId
    && site.radioMaxTxPowerDbm === selected.maxTxPowerDbm
    && numberArraysEqual(site.radioChannelWidthsMHz, selected.channelWidthsMHz)
    && site.radioReceiverSensitivityDbm === selected.receiverSensitivityDbm
    && site.radioMaxThroughputMbps === selected.maxThroughputMbps
    && site.radioMaxRangeKm === selected.maxRangeKm
    && site.radioPolarization === selected.polarization
    && performanceProfilesEqual(site.radioPerformanceProfiles, selected.performanceProfiles)
    && site.antennaModelId === selected.antennaId
    && site.antennaName === selected.antennaModel;
}

function recommendationTxPowerDbm(site: DesignSite, selected: EquipmentRecommendation): number {
  const recommendedMaximumDbm = Math.min(selected.recommendedTxPowerDbm, selected.maxTxPowerDbm);
  const requestedDbm = site.radioDeviceId === selected.deviceId ? Math.min(site.txPowerDbm, recommendedMaximumDbm) : recommendedMaximumDbm;
  return Math.max(-10, requestedDbm);
}

export function applyAutoEquipmentRecommendations(
  sites: DesignSite[],
  endpointIds: readonly string[],
  startSiteId: string,
  requestedChannelWidthMHz: number,
  recommendation: EquipmentRecommendation,
  peerRecommendation: EquipmentRecommendation | null,
): DesignSite[] {
  const endpointSet = new Set(endpointIds);
  let changed = false;
  const updated = sites.map((site) => {
    if (!endpointSet.has(site.id)) return site;
    const selected = recommendationForSite(site.id, startSiteId, recommendation, peerRecommendation);
    if (!selected || selected.channelWidthMHz !== requestedChannelWidthMHz) return site;
    const equipmentName = `${selected.manufacturerName} ${selected.deviceModel}`;
    const next: DesignSite = {
      ...site,
      equipmentName: displayEquipmentName(equipmentName),
      radioDeviceId: selected.deviceId,
      radioMaxTxPowerDbm: selected.maxTxPowerDbm,
      radioChannelWidthsMHz: [...selected.channelWidthsMHz],
      radioReceiverSensitivityDbm: selected.receiverSensitivityDbm,
      radioMaxThroughputMbps: selected.maxThroughputMbps,
      radioMaxRangeKm: selected.maxRangeKm,
      radioPolarization: selected.polarization,
      radioPerformanceProfiles: structuredClone(selected.performanceProfiles),
      antennaModelId: selected.antennaId,
      antennaName: selected.antennaModel,
      antennaGainDbi: selected.antennaGainDbi,
      txPowerDbm: recommendationTxPowerDbm(site, selected),
      frequencyGHz: selected.frequencyMHz / 1_000,
      channelWidthMHz: requestedChannelWidthMHz,
    };
    if (siteMatchesRecommendationSnapshot(site, next, selected)) return site;
    changed = true;
    return next;
  });
  return changed ? updated : sites;
}

function normalizedEquipmentName(value: string): string {
  return value.trim().replace(/\s+/gu, ' ').toLocaleLowerCase('en-US');
}

export function equipmentMaximumTxPowerDbm(site: DesignSite, radios: RadioDeviceRecord[]): number | null {
  if (site.radioMaxTxPowerDbm === null) return null;
  const catalogDevice = site.radioDeviceId ? radios.find((radio) => radio.id === site.radioDeviceId) : undefined;
  const catalogLimit = catalogDevice?.maxTxPowerDbm;
  if (site.radioMaxTxPowerDbm !== undefined) {
    return Math.min(
      Math.max(0, site.radioMaxTxPowerDbm),
      typeof catalogLimit === 'number' ? catalogLimit : UNVERIFIED_TX_SAFETY_CEILING_DBM,
      UNVERIFIED_TX_SAFETY_CEILING_DBM,
    );
  }
  if (site.radioDeviceId) {
    return typeof catalogLimit === 'number' ? Math.min(catalogLimit, UNVERIFIED_TX_SAFETY_CEILING_DBM) : null;
  }
  const equipmentName = normalizedEquipmentName(site.equipmentName);
  const device = radios.find((radio) => normalizedEquipmentName(`${radio.manufacturerName} ${radio.model}`) === equipmentName
    || normalizedEquipmentName(radio.model) === equipmentName);
  return typeof device?.maxTxPowerDbm === 'number' ? Math.min(device.maxTxPowerDbm, UNVERIFIED_TX_SAFETY_CEILING_DBM) : null;
}

export function maximumSiteTxPowerDbm(site: DesignSite, radios: RadioDeviceRecord[]): number | null {
  const equipmentLimitDbm = equipmentMaximumTxPowerDbm(site, radios);
  return equipmentLimitDbm === null ? null : Math.round(equipmentLimitDbm * 10) / 10;
}

export function clampSiteTxPowerDbm(site: DesignSite, requestedPowerDbm: number, radios: RadioDeviceRecord[]): number {
  if (!Number.isFinite(requestedPowerDbm)) return site.txPowerDbm;
  const equipmentLimitDbm = maximumSiteTxPowerDbm(site, radios);
  const upperLimitDbm = equipmentLimitDbm ?? Math.min(site.txPowerDbm, UNVERIFIED_TX_SAFETY_CEILING_DBM);
  return Math.max(-10, Math.min(upperLimitDbm, requestedPowerDbm));
}
